import React, { FunctionComponent } from "react";
import styled from "styled-components";

import { colours } from "utils";
import { CXL, CL } from "@/components/Typography";
import Device from "@/utils/device";
import StepForwardTitle from "../step-forward/StepForwardTitle";
import MarketingButton from "../MarketingButton";

const PILLARS = [
  {
    background: "/images/step-forward/blue-marble.png",
    textColour: colours.shell,
    titleBold: "Leading with",
    titleItalic: "Love",
  },
  {
    background: "/images/step-forward/purple-marble.png",
    textColour: colours.shell,
    titleBold: "Women with",
    titleItalic: "Bold Visions",
  },
  {
    background: "/images/step-forward/yellow-marble.png",
    textColour: colours.charcoal,
    titleBold: "Investing in",
    titleItalic: "Women",
  },
  {
    background: "/images/step-forward/tequila-sunrise-marble.png",
    textColour: colours.charcoal,
    titleBold: "Women",
    titleItalic: "& Wealth",
  },

  {
    background: "/images/step-forward/pale-marble.png",
    textColour: colours.charcoal,
    titleBold: "Gender Equality",
    titleItalic: "in Health",
  },
  {
    background: "/images/step-forward/violet-marble.png",
    textColour: colours.charcoal,
    titleBold: "Women's",
    titleItalic: "Mental Health",
  },
  {
    background: "/images/step-forward/light-blue-marble.png",
    textColour: colours.charcoal,
    titleBold: "The Champion's",
    titleItalic: "Mindset",
  },
  {
    background: "/images/step-forward/dark-blue-marble.png",
    textColour: colours.shell,
    titleBold: "AI",
    titleItalic: "& Career Advancement",
  },
  {
    background: "/images/step-forward/light-orange-marble.png",
    textColour: colours.charcoal,
    titleBold: "Women Driving",
    titleItalic: "Sustainability",
  },
];

type StyleProps = {
  background?: string;
  textColour?: string;
};

type PillarsContentProps = {
  handleClick: (e) => void;
};

const PillarsContent: FunctionComponent<PillarsContentProps> = (props) => {
  return (
    <S.Section data-cy="SF-content-themes">
      <S.CopyContainer>
        <StepForwardTitle>
          <S.FontWeightWrap>
            Topics on <br />
            the{" "}
          </S.FontWeightWrap>
          <S.ItalicWrap>agenda</S.ItalicWrap>
        </StepForwardTitle>
        <CXL>
          <b>The themes we’re Stepping Forward for in 2025: </b>
        </CXL>
      </S.CopyContainer>
      <S.PillarsContainer>
        {PILLARS.map((pillar) => (
          <S.PillarCircle {...pillar} key={pillar.titleBold}>
            <S.PillarTitle {...pillar}>
              {pillar.titleBold}
              <br />
              <S.RoxboroughFontWrap>{pillar.titleItalic}</S.RoxboroughFontWrap>
            </S.PillarTitle>
          </S.PillarCircle>
        ))}
      </S.PillarsContainer>
      <S.CopyContainer>
        <CXL weight={700}>
          Our full agenda is coming shortly, check back here soon for more...
        </CXL>
        <MarketingButton
          name="access"
          buttonText="Secure your spot"
          colour={colours.stepForwardBlue}
          textColour={colours.stepForwardBlue}
          animationColour={colours.stepForwardOrange}
          onClick={props.handleClick}
        />
      </S.CopyContainer>
    </S.Section>
  );
};

const S = () => {};

S.Section = styled.section`
  position: relative;
  width: 100%;
  padding: 10%;

  @supports not (-webkit-touch-callout: none) {
    background-attachment: fixed;
  }

  @media ${Device.tablet} {
    padding: 5% 10%;
  }
`;

S.CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > ${CXL} {
    line-height: 2rem;
  }

  @media ${Device.mobile} {
    gap: 30px;

    > ${CXL} {
      line-height: 2.5rem;
    }
  }

  @media ${Device.desktop} {
    width: 60%;
    margin: 0 60px 0 0;
    gap: 30px;

    > ${CXL} {
      line-height: 2.8rem;
    }
  }
`;

S.ItalicWrap = styled.span`
  font-style: italic;
`;

S.FontWeightWrap = styled.span`
  font-weight: 100;
`;

S.PillarsContainer = styled.div`
  margin: 40px 0;
  width: 100%;
  padding: 0 5%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: 10px;

  > * > ${CL} {
    line-height: 1.6rem;
  }

  @media ${Device.mobile} {
    > * > ${CL} {
      line-height: 2.5rem;
    }
  }

  @media ${Device.tablet} {
    margin: 60px 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-gap: 20px;
  }

  @media ${Device.desktop} {
    padding: 0 10%;
    > * > ${CL} {
      line-height: 2.8rem;
    }
  }
`;

S.PillarCircle = styled.div<StyleProps>`
  background-image: url(${(p) => p.background});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 50%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;

  @supports not (aspect-ratio: 1) {
    &::before {
      float: left;
      padding-top: 100%;
      content: "";
    }
    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }
`;

S.PillarTitle = styled.h3<StyleProps>`
  color: ${(p) => p.textColour};
  text-align: center;
  font-size: 1.5rem;
  line-height: 1.5rem;

  @media ${Device.mobile} {
    font-size: 2.5rem;
    line-height: 2.5rem;
  }

  @media ${Device.desktop} {
    font-size: 3.7rem;
    line-height: 4rem;
  }
`;

S.RoxboroughFontWrap = styled.span`
  font-family:
    Roxborough CF,
    sans-serif;
  font-weight: 100;
  font-style: italic;
`;

export default PillarsContent;
